import React from 'react';
import { FormattedMessage } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import { IconButton } from '@bibliocommons/base-buttons';
import { ZoomIn, ZoomOut, Print } from '@bibliocommons/base-icons';
import { openPopUpWindow } from '@bibliocommons/utils-browser';
import { WarningAlert } from '@bibliocommons/alerts';
import Svg from '@bibliocommons/svg';

import PanZoom from 'app/components/shared/PanZoom';
import stackMapStateShape from 'app/shapes/stackMapStateShape';

import './StackMapDisplay.scss';

const MAX_SCALE_FACTOR = 5;
const STACK_MAP_WINDOW_NAME = 'stack-map';

function renderPlaceHolder() {
  return (
    <Svg className="stack-map-placeholder" width="709" height="96" preserveAspectRatio="none" viewBox="0 0 709 96">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M365,84 L365,96 L0,96 L0,84 L365,84 Z M650,60 L650,72 L0,72 L0,60 L650,60 Z M709,36 L709,48 L0,48 L0,36 L709,36 Z M155,0 L155,16 L0,16 L0,0 L155,0 Z"
      />
    </Svg>
  );
}

export default class StackMapDisplay extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      x: 0,
      y: 0,
      scale: 1
    };
    bindAll(this);
  }

  handleMapUpdate(coordinates) {
    this.setState(coordinates);
  }

  handleZoomIn() {
    this.setState(state => ({
      scale: Math.min(MAX_SCALE_FACTOR, state.scale + 0.5)
    }));
  }

  handleZoomOut() {
    this.setState(state => {
      const newScale = Math.max(1, state.scale - 0.5);
      return {
        scale: newScale,
        x: newScale !== 1 ? state.x : 0,
        y: newScale !== 1 ? state.y : 0
      };
    });
  }

  handlePrint() {
    const { stackMapState } = this.props;

    const { results, settings } = stackMapState;
    const { callno, library, location } = results;
    const { domain } = settings;

    const queryParams = new URLSearchParams({
      callno,
      library,
      location,
      v: 'pf'
    }).toString();

    openPopUpWindow({
      url: `https://${domain}.stackmap.com/view/?${queryParams}`,
      STACK_MAP_WINDOW_NAME,
      windowFeatures: {
        height: 800,
        width: 950,
        directories: 0
      }
    });
  }

  renderMapControls() {
    const zoomIn = (
      <IconButton className="zoom-in" dataKey="zoom-in-button" handleClick={this.handleZoomIn}>
        <ZoomIn />
      </IconButton>
    );

    const zoomOut = (
      <IconButton className="zoom-out" dataKey="zoom-out-button" handleClick={this.handleZoomOut}>
        <ZoomOut />
      </IconButton>
    );

    const print = (
      <IconButton className="print" dataKey="print-button" handleClick={this.handlePrint}>
        <Print />
      </IconButton>
    );

    return (
      <div className="stack-map-controls">
        {zoomIn}
        {zoomOut}
        {print}
      </div>
    );
  }

  renderImage() {
    const { stackMapState } = this.props;

    const map = stackMapState.results?.maps?.[0];
    if (!map) {
      return null;
    }

    const { scale, x, y } = this.state;

    return (
      <div className="stack-map-panel" aria-hidden>
        <PanZoom
          className="stack-map-image-wrapper"
          handleUpdate={this.handleMapUpdate}
          height={map.height}
          maxScale={MAX_SCALE_FACTOR}
          scale={scale}
          x={x}
          y={y}
          width={map.width}
        >
          <img
            className="stack-map-image"
            data-key="stack-map-image"
            alt={map.library}
            src={`${map.mapurl}&marker=1`}
            loading="eager"
          />
        </PanZoom>
      </div>
    );
  }

  renderContent() {
    const { stackMapState } = this.props;
    const { hasError, results, settings } = stackMapState;

    const map = results?.maps?.[0];

    if (hasError || !map) {
      return (
        <WarningAlert className="failure-warning">
          <FormattedMessage id="stack_map_failure" />
        </WarningAlert>
      );
    }

    const isShowRanges = settings.hideRanges;

    const callNumber = results.callno;
    const collection = results.location;

    const directions = map.directions;
    const floorName = map.floorname;
    const ranges = map.ranges || [];

    const hasGeneralInfo = Boolean(directions || callNumber || collection);

    return (
      <section>
        <div className="shelf-details">
          {hasGeneralInfo && (
            <div className="shelf-details-general">
              {directions && <p className="shelf-detail shelf-detail--primary">{directions}</p>}
              {(callNumber || collection) && (
                <p className="shelf-detail shelf-detail--secondary">
                  {callNumber && (
                    <>
                      <FormattedMessage defaultMessage="Call number" id="CALL_NUMBER" />: {callNumber}
                      <br />
                    </>
                  )}
                  {collection && (
                    <>
                      <FormattedMessage defaultMessage="Collection" id="collection" />: {collection}
                    </>
                  )}
                </p>
              )}
            </div>
          )}
          {isShowRanges ? (
            <ul className="ranges">
              {ranges.map(range => {
                const section = range.rangename;
                const callNumberStart = range.startcallno;
                const callNumberEnd = range.endcallno;

                return (
                  <li className="shelf-detail shelf-detail--primary" key={`${callNumberStart}-${callNumberEnd}`}>
                    <FormattedMessage
                      defaultMessage="{floorName}. Section: {section}. Call numbers from ''{callNumberStart}'' to ''{callNumberEnd}''."
                      id="stackmap_floor_location"
                      values={{ callNumberEnd, callNumberStart, floorName, isFloorOnly: false, section }}
                    />
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="shelf-detail shelf-detail--primary">
              <FormattedMessage
                defaultMessage="{floorName}."
                id="stackmap_floor_location"
                values={{ floorName, isFloorOnly: true }}
              />
            </p>
          )}
          <p className="shelf-detail shelf-detail--secondary">
            <FormattedMessage defaultMessage="The red pin indicates the location of the item." id="stackmap_legend" />
          </p>
        </div>
        {this.renderMapControls()}
        {this.renderImage()}
        <div className="powered-by">
          <FormattedMessage id="stackmap_powered_by" />
        </div>
      </section>
    );
  }

  render() {
    const { stackMapState } = this.props;
    const { isFetching } = stackMapState;

    return <div className="cp-stack-map-display">{isFetching ? renderPlaceHolder() : this.renderContent()}</div>;
  }
}

StackMapDisplay.propTypes = {
  stackMapState: stackMapStateShape.isRequired
};
